/** Shaping and formatting for the Plan My Week pop-out (F3.1-F3.7).
 *
 *  Split out from the panel so the rules that are easy to get wrong -- and that
 *  carry real meaning -- are unit-testable without mounting React.
 */
import type { DriveLeg, PlanEdit, PlannedStop, SalesViewMapPoint, TripDrive, WeekPlanBlob } from '../../api/types';
import type { NavApp } from '../../maps/navPreference';
import { webUrl } from '../../maps/mapsLink';

/** A trip is a set of stops worth driving between. It is NOT a day.
 *
 *  `tripNo === null` is the calls group: a CALL has no trip and no position in
 *  one. Rendering calls as "trip 0" would invite a rep to drive to them. */
export interface PlanGroup<T extends PlannedStop = PlannedStop> {
  tripNo: number | null;
  stops: T[];
  miles: number;
}

/** Group stops into trips, calls last.
 *
 *  Stops are ordered by `stop_no` WITHIN a trip only. Sorting the whole list by
 *  stop_no would interleave trips, which reads as one impossible drive. */
export function groupByTrip<T extends PlannedStop>(stops: T[]): PlanGroup<T>[] {
  const trips = new Map<number | null, T[]>();
  for (const stop of stops) {
    const key = stop.contact_mode === 'CALL' ? null : stop.trip_no;
    const bucket = trips.get(key);
    if (bucket) bucket.push(stop);
    else trips.set(key, [stop]);
  }

  const groups: PlanGroup<T>[] = [];
  for (const [tripNo, list] of trips) {
    const sorted = [...list].sort((a, b) => (a.stop_no ?? 0) - (b.stop_no ?? 0));
    groups.push({
      tripNo,
      stops: sorted,
      // Only miles we actually know. A null leg is unknown, not zero.
      miles: sorted.reduce((sum, s) => sum + (s.miles_from_previous ?? 0), 0),
    });
  }

  return groups.sort((a, b) => {
    if (a.tripNo === null) return 1;
    if (b.tripNo === null) return -1;
    return a.tripNo - b.tripNo;
  });
}

/** Miles for one leg.
 *
 *  NULL MEANS THE CLIENT COULD NOT BE PLACED, and must never render as 0 -- zero
 *  reads as "next door", which is the opposite of "we don't know where this is"
 *  and would send a rep down the wrong road. */
export function formatMiles(miles: number | null | undefined): string {
  if (miles === null || miles === undefined) return '—';
  if (miles === 0) return 'same ZIP';
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
}

export function formatDuration(minutes: number): string {
  const m = Math.round(minutes);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
}

/** How a leg or trip figure should read. `straight` must be visibly marked: a crow-flies
 *  distance passed off as a drive is the under-estimate the rep asked us never to give. */
export interface DriveText {
  text: string;
  kind: 'drive' | 'straight' | 'unknown' | 'start' | 'unpriced';
}

/** The drive INTO a stop (CSE-20). Falls back to the planner's miles on an unpriced plan. */
export function formatLeg(stop: PlannedStop): DriveText {
  if (!('leg' in stop)) {
    return {
      text: formatMiles(stop.miles_from_previous),
      kind: stop.miles_from_previous == null ? 'unknown' : 'unpriced',
    };
  }
  const leg = stop.leg;
  if (!leg) {
    return stop.is_anchor || stop.stop_no === 1
      ? { text: 'start', kind: 'start' }
      : { text: '—', kind: 'unknown' };
  }
  return formatDriveLeg(leg, stop.leg_from);
}

export function formatDriveLeg(leg: DriveLeg, from?: string | null): DriveText {
  const suffix = from ? ` from ${from}` : '';
  if (leg.source === 'drive') {
    return { text: `est. ${formatMiles(leg.miles)} · ${formatDuration(leg.minutes ?? 0)}${suffix}`, kind: 'drive' };
  }
  return { text: `~${formatMiles(leg.miles)} straight-line${suffix}`, kind: 'straight' };
}

export function formatTripDrive(trip: TripDrive): DriveText {
  if (trip.miles == null) return { text: '—', kind: 'unknown' };
  if (trip.estimate === 'drive') {
    return { text: `est. ${formatMiles(trip.miles)} · ${formatDuration(trip.minutes ?? 0)}`, kind: 'drive' };
  }
  return {
    text: `~${formatMiles(trip.miles)} ${trip.estimate === 'mixed' ? 'partly straight-line' : 'straight-line'}`,
    kind: 'straight',
  };
}

export function formatLocation(stop: PlannedStop): string {
  const parts = [stop.city, stop.state].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

/** Google Maps directions links take 9 waypoints plus a destination (CSE-19). Same value as
 *  `_MAPS_STOPS_PER_LINK` in sales_email_service.py. */
export const MAPS_STOPS_PER_LINK = 10;

/** The street address when we have one ("123 Main St, City, ST 12345"); the dealer name only
 *  when we do not. Searching by name finds the wrong business as often as the right one.
 *  Same rule as `_stop_query` in sales_email_service.py, so the app and the email agree. */
export function stopQuery(stop: PlannedStop): string {
  const region = [stop.state, stop.zip].filter(Boolean).join(' ');
  if (stop.street) return [stop.street, stop.city, region].filter(Boolean).join(', ');
  return [stop.client_name, stop.city, region].filter(Boolean).join(', ');
}

/** A trip's stops in order as Google Maps directions; a long trip continues in a second link.
 *  Always the web directions page, never an app scheme: the multi-stop app formats
 *  (`comgooglemaps://...daddr=a+to:b`, the legacy Android `daddr=...+to:`) are undocumented and
 *  can fail inside the app with nothing to fall back to, while Google's own page shows every
 *  stop and offers "Open in the app" itself (APP-1). */
export function googleTripUrls(stops: PlannedStop[]): string[] {
  const queries = stops.map(stopQuery).filter(Boolean);
  const urls: string[] = [];
  let origin: string | null = null;
  for (let start = 0; start < queries.length; start += MAPS_STOPS_PER_LINK) {
    const chunk = queries.slice(start, start + MAPS_STOPS_PER_LINK);
    const params = new URLSearchParams({ api: '1', travelmode: 'driving', destination: chunk[chunk.length - 1] });
    if (origin) params.set('origin', origin);
    if (chunk.length > 1) params.set('waypoints', chunk.slice(0, -1).join('|'));
    urls.push(`https://www.google.com/maps/dir/?${params.toString()}`);
    origin = chunk[chunk.length - 1];
  }
  return urls;
}

/** "Take Me There" in one named app: that app's own direct link (no SIFT page in between). A
 *  phone opens it in the app when installed, else the app's web page, which offers the app. */
export function navStopUrl(stop: PlannedStop, app: NavApp): string | null {
  const query = stopQuery(stop);
  if (!query) return null;
  return webUrl({ provider: app, stops: [query] });
}

/** "Take Me There" handed to the phone itself (Android): opens the default navigation app, or
 *  the system "Open with" sheet listing every installed one. */
export function geoStopUrl(stop: PlannedStop): string | null {
  const query = stopQuery(stop);
  if (!query) return null;
  return `geo:0,0?q=${encodeURIComponent(query)}`;
}

/** True when the plan has NO frozen baseline, so changes are measured against a
 *  recommendation that moves with the book rather than against a plan.
 *
 *  This is now the EXCEPTION, not the norm: a week with no batch record is frozen on
 *  first open and becomes the plan of record. `generated_on_read` means the freeze
 *  itself failed, so the caller must not present it as adherence. */
export function isLiveDiff(source: string | undefined): boolean {
  return source !== 'plan_of_record';
}

/** True when the week has not started, so there is nothing to adhere to YET.
 *
 *  Distinct from a failed freeze: a preview is deliberately not frozen, because
 *  freezing next week now would pin its baseline to today's book. */
export function isPreview(source: string | undefined): boolean {
  return source === 'preview';
}

/** Today in Eastern, as an ISO date.
 *
 *  The week boundary is an ET business fact, not a local one: a rep in California at 10pm
 *  Sunday is still in last week, and one in London on Monday morning is not yet in the new
 *  one. The backend agrees (`sales_plan_store.today_et`), and both have to, or the frontend
 *  asks for a week the backend does not think it is. */
export function todayEt(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

/** The Monday of the week `iso` falls in, as an ISO date.
 *
 *  Arithmetic is done in UTC on a date-only string so it cannot drift a day: the value is
 *  already an ET calendar date by the time it gets here. */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

/** Today and next Monday, for the week toggle. */
export function weekOptions(today: string): { current: string; next: string } {
  const current = mondayOf(today);
  const next = new Date(`${current}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 7);
  return { current, next: next.toISOString().slice(0, 10) };
}

/** True once next week has been built and frozen, which happens Friday.
 *
 *  Before that a preview is a genuine guess: Friday's run rebuilds it from that morning's
 *  book, so anything a rep plans around on Thursday can still move. */
export function prebuildDone(today: string, prebuildWeekday = 5): boolean {
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay();  // 0 Sun … 5 Fri
  return dow === 0 || dow >= prebuildWeekday;
}

/** CSV for the route sheet. Quoted throughout, because client names contain
 *  commas ("Smith, Jones & Co") and a raw join silently shifts every column. */
export function toCsv(plan: WeekPlanBlob): string {
  const header = [
    'trip', 'stop', 'client_id', 'client_name', 'contact_mode', 'visit_type',
    'city', 'state', 'zip', 'miles_from_previous', 'tier', 'priority_score', 'reasons',
  ];
  const cell = (v: unknown): string => {
    if (v === null || v === undefined) return '""';
    return `"${String(v).replace(/"/g, '""')}"`;
  };
  const rows = groupByTrip(plan.stops).flatMap((group) =>
    group.stops.map((s) => [
      group.tripNo === null ? 'calls' : group.tripNo,
      s.stop_no ?? '',
      s.client_id,
      s.client_name,
      s.contact_mode,
      s.visit_type,
      s.city,
      s.state,
      s.zip,
      // Empty, not 0, so a spreadsheet does not average an unknown leg as zero.
      s.miles_from_previous ?? '',
      s.tier,
      s.priority_score.toFixed(3),
      s.reasons.join('; '),
    ].map(cell).join(',')),
  );
  return [header.map(cell).join(','), ...rows].join('\n');
}

/** Distinct enough to tell apart at a glance, and colour-blind safe. */
export const TRIP_COLORS = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#db2777', '#0891b2'];

export function tripColor(tripNo: number): string {
  return TRIP_COLORS[(tripNo - 1) % TRIP_COLORS.length];
}

/** One trip's drive, ready for a Leaflet Polyline. */
export interface RouteLine {
  tripNo: number;
  color: string;
  positions: Array<[number, number]>;
  /** Stops in this trip we could not put on the map. */
  missing: string[];
}

export interface PlanRoute {
  lines: RouteLine[];
  /** Client ids in the plan with no coordinates, across every trip. A stop the
   *  rep can see in the table but not on the map needs an answer, not a silent
   *  omission. */
  unplottable: string[];
  /** True when a line SKIPS a stop that sits between two plotted ones, so the
   *  drawn route is shorter than the real drive. Must be said out loud. */
  hasGaps: boolean;
}

/** Join plan stops to map points by `client_id`.
 *
 *  A `PlannedStop` carries city/state/zip but NO coordinates -- the geo payload
 *  is a separate call -- so the route only exists where the two agree.
 *
 *  CALLS ARE EXCLUDED. A call has no place in a drive, and drawing one would
 *  put a line through a client the rep is not travelling to. */
export function buildRoute(
  stops: PlannedStop[],
  points: SalesViewMapPoint[],
): PlanRoute {
  const coords = new Map<string, [number, number]>();
  for (const p of points) {
    if (p.latitude != null && p.longitude != null) {
      coords.set(p.client_id, [p.latitude, p.longitude]);
    }
  }

  const lines: RouteLine[] = [];
  const unplottable: string[] = [];
  let hasGaps = false;

  for (const group of groupByTrip(stops)) {
    if (group.tripNo === null) continue;

    const positions: Array<[number, number]> = [];
    const missing: string[] = [];
    let seenPlotted = false;
    let pendingGap = false;

    for (const stop of group.stops) {
      const point = coords.get(stop.client_id);
      if (point) {
        // A stop we skipped now sits BETWEEN two drawn points, so the line
        // understates the drive rather than merely omitting a tail.
        if (pendingGap && seenPlotted) hasGaps = true;
        pendingGap = false;
        seenPlotted = true;
        positions.push(point);
      } else {
        missing.push(stop.client_id);
        unplottable.push(stop.client_id);
        pendingGap = true;
      }
    }

    // Two points minimum: one plotted stop is a marker, not a drive.
    if (positions.length >= 2) {
      lines.push({
        tripNo: group.tripNo,
        color: tripColor(group.tripNo),
        positions,
        missing,
      });
    }
  }

  return { lines, unplottable, hasGaps };
}

/** Stops sharing one map position, in plan order. */
export interface StopCluster {
  key: string;
  point: [number, number];
  stops: PlannedStop[];
}

/** Group plan stops that land on the SAME point.
 *
 *  Clients are plotted on their ZIP centroid, so a rep with three stops in one ZIP
 *  gets three pins at identical coordinates -- the last drawn hides the rest, and
 *  nothing on the map says the others are there. Grouping them lets the map show a
 *  count and fan them out on demand, the same as the cluster views.
 *
 *  Rounded to 5dp (~1m): centroids for one ZIP are byte-identical in practice, but
 *  float arithmetic upstream should not be able to split a group. */
export function groupStopsByLocation(
  stops: PlannedStop[],
  coords: Map<string, [number, number]>,
): StopCluster[] {
  const groups = new Map<string, StopCluster>();
  for (const stop of stops) {
    const point = coords.get(stop.client_id);
    if (!point) continue;
    const key = `${point[0].toFixed(5)},${point[1].toFixed(5)}`;
    const existing = groups.get(key);
    if (existing) existing.stops.push(stop);
    else groups.set(key, { key, point, stops: [stop] });
  }
  return [...groups.values()];
}

/** How a stop differs from what was recommended. */
export type PlanChange = 'kept' | 'added' | 'moved' | 'dropped';

export interface DisplayStop extends PlannedStop {
  change: PlanChange;
}

/** The plan as ONE list: the recommendation and the rep's overrides, merged.
 *
 *  A dropped stop is KEPT IN PLACE, greyed rather than removed. Deleting it leaves the
 *  rep with no way to see or undo the decision, and it is why "what changed" had to be a
 *  second table repeating the same clients in different words.
 *
 *  Order comes from `actual`, so the live plan reads top to bottom; dropped stops are
 *  slotted back into the trip they were recommended for.
 */
export function mergeForDisplay(
  recommended: WeekPlanBlob | undefined,
  actual: WeekPlanBlob | undefined,
  updates: PlanEdit[],
): DisplayStop[] {
  const rec = recommended?.stops ?? [];
  const act = actual?.stops ?? [];
  const recById = new Map(rec.map((s) => [s.client_id, s]));
  const actById = new Map(act.map((s) => [s.client_id, s]));
  const editByClient = new Map(updates.map((u) => [u.client_id, u]));

  const classify = (stop: PlannedStop): PlanChange => {
    const before = recById.get(stop.client_id);
    if (!before) return 'added';
    if (before.trip_no !== stop.trip_no
      || before.stop_no !== stop.stop_no
      || before.contact_mode !== stop.contact_mode) return 'moved';
    return 'kept';
  };

  const out: DisplayStop[] = act.map((s) => ({ ...s, change: classify(s) }));

  // Anything recommended but no longer in the plan. Only counted as dropped when the rep
  // actually dropped it -- a stop missing because the week was regenerated is not a decision.
  for (const stop of rec) {
    if (actById.has(stop.client_id)) continue;
    if (editByClient.get(stop.client_id)?.action !== 'dropped') continue;
    out.push({ ...stop, change: 'dropped' });
  }
  return out;
}

/** Words for the badge. `kept` gets none: the unchanged case is the quiet one. */
export function changeLabel(change: PlanChange): string | null {
  if (change === 'added') return 'you added';
  if (change === 'moved') return 'you moved';
  if (change === 'dropped') return 'you dropped';
  return null;
}

type SpreadValues = { max_radius_mi: number; max_drive_min: number; urgency_pull_mi: number; same_area_mi: number };

/** "How far a trip spreads": one choice that sets the four distance settings in step. Set one at
 *  a time they contradicted each other (a 30 mi "area" around a 15 mi trip). Tight measured on
 *  James C.'s NYC book: week drive 277 -> 204 min against Standard, same 30 visits. */
export const SPREAD_PRESETS: Array<{ id: string; label: string; hint: string; values: SpreadValues }> = [
  { id: 'tight', label: 'Tight \u2014 dense city', hint: 'Bridges and traffic: trips stay within 10 miles and 25 minutes. Shortest drives, but an urgent client across town may wait for a later trip.', values: { max_radius_mi: 10, max_drive_min: 25, urgency_pull_mi: 25, same_area_mi: 8 } },
  { id: 'standard', label: 'Standard \u2014 suburban', hint: 'The default.', values: { max_radius_mi: 15, max_drive_min: 30, urgency_pull_mi: 25, same_area_mi: 10 } },
  { id: 'wide', label: 'Wide \u2014 spread-out metro', hint: 'Longer highway hops between stops.', values: { max_radius_mi: 30, max_drive_min: 45, urgency_pull_mi: 45, same_area_mi: 20 } },
  { id: 'rural', label: 'Rural', hint: 'Long drives are normal; trips reach an hour or more.', values: { max_radius_mi: 60, max_drive_min: 75, urgency_pull_mi: 90, same_area_mi: 40 } },
];

export function spreadPresetOf(capacity: Partial<Record<keyof SpreadValues, number>>): string {
  const found = SPREAD_PRESETS.find((p) => (Object.keys(p.values) as Array<keyof SpreadValues>)
    .every((k) => Number(capacity[k]) === p.values[k]));
  return found?.id ?? 'custom';
}
